/**
 * Sendbird client singleton (Phase 2a).
 *
 * - Uses the low-level `@sendbird/chat` SDK for the notification-bell unread count
 *   and any programmatic channel actions we need.
 * - The `@sendbird/uikit-react` `<SendbirdProvider>` manages its own connection
 *   inside the ChatSendbird page — the two connections coexist under the same
 *   userId because Sendbird supports multiple client connections per user.
 */
import SendbirdChat from "@sendbird/chat";
import { GroupChannelModule } from "@sendbird/chat/groupChannel";
import { api } from "@/api/client";

let sdk = null;
let initPromise = null;
let currentUserId = null;

const APP_ID = process.env.REACT_APP_SENDBIRD_APP_ID || "";

export const SENDBIRD_APP_ID = APP_ID;

export async function fetchSessionToken() {
    const { data } = await api.post("/sendbird/session-token");
    return data; // { app_id, user_id, nickname, session_token, expires_at }
}

export async function connectSendbird({ userId, sessionToken }) {
    if (!APP_ID) throw new Error("REACT_APP_SENDBIRD_APP_ID not configured");
    // Return existing connection if same user
    if (sdk && currentUserId === userId) return sdk;
    if (initPromise) return initPromise;

    initPromise = (async () => {
        try {
            sdk = SendbirdChat.init({
                appId: APP_ID,
                modules: [new GroupChannelModule()],
                localCacheEnabled: false,
            });
            await sdk.connect(userId, sessionToken);
            currentUserId = userId;
            return sdk;
        } catch (err) {
            initPromise = null;
            sdk = null;
            currentUserId = null;
            throw err;
        }
    })();
    return initPromise;
}

export function getSdk() {
    return sdk;
}

export async function disconnectSendbird() {
    try {
        if (sdk) await sdk.disconnect();
    } catch (_e) { /* noop */ }
    sdk = null;
    currentUserId = null;
    initPromise = null;
}

export function getCustomApiHost() {
    return APP_ID ? `https://api-${APP_ID}.sendbird.com` : undefined;
}
